import { MapPin, ExternalLink } from 'lucide-react';

interface LocationBubbleProps {
  lat: number;
  lng: number;
  address: string;
  isOwn: boolean;
}

export function LocationBubble({ lat, lng, address, isOwn }: LocationBubbleProps) {
  const appleMapsUrl = `https://maps.apple.com/?ll=${lat},${lng}&q=${encodeURIComponent(address)}`;
  const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;

  // OSM tile coordinates for zoom level 15
  const zoom = 15;
  const x = Math.floor(((lng + 180) / 360) * Math.pow(2, zoom));
  const y = Math.floor(((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2) * Math.pow(2, zoom));
  const tileUrl = `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;

  return (
    <div className="rounded-xl overflow-hidden w-[240px]">
      {/* Map tile preview */}
      <div className="relative h-[120px] bg-muted">
        <img
          src={tileUrl}
          alt="Map location"
          className="w-full h-full object-cover"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full bg-primary/90 flex items-center justify-center shadow-lg">
            <MapPin className="h-4 w-4 text-primary-foreground" />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="px-2.5 py-2">
        <p className={`text-xs leading-snug line-clamp-2 mb-2 ${isOwn ? 'text-white/80' : 'text-foreground/80'}`}>
          {address}
        </p>

        {/* Map links */}
        <div className="flex gap-1.5">
          <a
            href={appleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full transition-colors ${
              isOwn
                ? 'bg-white/15 text-white/80 hover:bg-white/25'
                : 'bg-muted/80 text-foreground/70 hover:bg-muted'
            }`}
          >
            🍎 Apple Maps
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full transition-colors ${
              isOwn
                ? 'bg-white/15 text-white/80 hover:bg-white/25'
                : 'bg-muted/80 text-foreground/70 hover:bg-muted'
            }`}
          >
            📍 Google Maps
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
